import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { GridStack } from 'gridstack';
import { GridstackComponent, GridstackModule, NgGridStackOptions, NgGridStackWidget } from 'gridstack/dist/angular';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzNotificationModule, NzNotificationService } from 'ng-zorro-antd/notification';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { MatchingRule, MatchingRuleService } from '../../services/matching-rule/matching-rule.service';
import { VariablesService } from '../../services/variables/variables.service';
import { DEFAULT_VEHICLE_GRID, IColumnDefinition, TAB_ATTRIBUTE_PREFIX } from './standard-tile.model';
import { TileInfoItemComboComponent } from './tile-info-item-combo/tile-info-item-combo.component';
import { TileInfoItemComponent } from './tile-info-item/tile-info-item.component';

const LEGACY_SELECTOR_MAP: Record<string, string> = {
  'tile-info-item': 'tt-vehicle-info-item',
  'tile-info-item-combo': 'tt-vehicle-info-item-combo',
};

@Component({
  selector: 'standard-tile',
  standalone: true,
  imports: [CommonModule, FormsModule, GridstackModule, NzSelectModule, NzButtonModule, NzIconModule, NzSpinModule, NzNotificationModule],
  templateUrl: './standard-tile.component.html',
  styleUrl: './standard-tile.component.scss'
})
export class StandardTileComponent implements OnInit, AfterViewInit {
  variablesService = inject(VariablesService);
  matchingRuleService = inject(MatchingRuleService);
  notification = inject(NzNotificationService);

  tabs: MatchingRule[] = ['cardealer', 'leasing', 'fleet'];
  selectedType: MatchingRule = 'cardealer';

  @ViewChild(GridstackComponent) gridComp?: GridstackComponent;

  columnDefinitions: IColumnDefinition[] = [];
  filteredColumnDefinitions: IColumnDefinition[] = [];

  loading = true;
  saving = false;
  gridReady = false;

  items: NgGridStackWidget[] = [];

  gridOpt: NgGridStackOptions = {
    column: 6,
    cellHeight: '70px',
    margin: 6,
    row: 3,
    removable: '.trash',
    float: true,
    itemClass: 'edit-item',
    acceptWidgets: true,
    children: [],
  };

  constructor() {
    GridstackComponent.addComponentToSelectorType([TileInfoItemComponent, TileInfoItemComboComponent]);
  }

  ngOnInit(): void {
    this.variablesService.getColumnDefinitions().subscribe({
      next: res => {
        this.columnDefinitions = res ?? [];
        this.updateFilteredColumns();
        this.loadTabSettings();
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.gridReady = true;
      this.refreshDragTemplates();
      setTimeout(() => {
        this.gridComp?.grid?.on('dropped', () => this.refreshDragTemplates());
      });
    });
  }

  onTypeChange(): void {
    this.updateFilteredColumns();
    this.loadTabSettings();
  }

  private refreshDragTemplates(): void {
    this.items = [
      { x: 0, y: 0, w: 2, selector: 'tt-vehicle-info-item', input: { cardConfig: null, columnDefinition: this.filteredColumnDefinitions } },
      { x: 0, y: 1, w: 3, selector: 'tt-vehicle-info-item-combo', input: { cardConfig: null, columnDefinition: this.filteredColumnDefinitions } },
    ];
    GridStack.setupDragIn('.sidebar-item', { helper: 'clone', appendTo: 'body' }, this.items);
  }

  private updateFilteredColumns(): void {
    const prefix = TAB_ATTRIBUTE_PREFIX[this.selectedType];
    this.filteredColumnDefinitions = this.columnDefinitions
      .filter(col => !!col[`${prefix}_tenant_default_attribute`])
      .sort((a, b) => (a.headerName ?? '').localeCompare(b.headerName ?? ''));
    if (this.gridReady) {
      this.refreshDragTemplates();
    }
  }

  private loadTabSettings(): void {
    this.loading = true;
    this.matchingRuleService.getMatchingRule('findview', this.selectedType).subscribe({
      next: res => {
        const children = ((res?.settings?.['vehicleGrid']) ?? []) as NgGridStackWidget[];
        children.forEach(child => {
          if (child.selector && LEGACY_SELECTOR_MAP[child.selector]) {
            child.selector = LEGACY_SELECTOR_MAP[child.selector];
          }
          child.input = { ...(child.input ?? {}), columnDefinition: this.filteredColumnDefinitions };
        });
        this.gridComp?.grid?.load(children, true);
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.gridComp?.grid?.load([], true);
        this.loading = false;
        if (err.status !== 404) {
          this.notification.error('Error', 'Failed to load the standard view for this tab.');
        }
      }
    });
  }

  resetToDefaults(): void {
    const children = DEFAULT_VEHICLE_GRID.map(item => ({
      ...item,
      input: { ...item.input, columnDefinition: this.filteredColumnDefinitions }
    })) as NgGridStackWidget[];
    this.gridComp?.grid?.load(children, true);
  }

  saveConfig(): void {
    this.saving = true;
    const serialized = (this.gridComp?.grid?.save() ?? []) as NgGridStackWidget[];
    serialized.forEach((el, idx) => {
      delete el.input?.['columnDefinition'];
      el.id = idx.toString();
    });
    this.matchingRuleService.postMatchingRule('findview', this.selectedType, { vehicleGrid: serialized }).subscribe({
      next: () => {
        this.saving = false;
        this.notification.success('Success', 'The standard view has been saved.');
      },
      error: err => {
        this.saving = false;
        this.notification.error('Error', err.message);
      }
    });
  }

}
